/**
 * Sensor map utilities — mirror the mobile app's sensor map helper.
 *
 * The sensorMapList in deviceInfoV2 describes every physical sensor module
 * attached to or built into the device. Each sensor data object (dcSensorList,
 * acSensorList, securitySensorlist, etc.) carries sourceAddressMSB/LSB fields
 * that identify which physical module owns that reading.
 *
 * Rules:
 *  - Built-in / integrated sensors have sourceAddressMSB ≤ 1 AND sourceAddressLSB ≤ 1
 *    (the internal XPulse bus address — always 1:1 for the first wired module).
 *  - Wireless sensors have higher address values assigned during pairing.
 */

import type { NASensorMapObject } from '@/api/models';

/**
 * Returns true if the sensor belongs to the device's internal XPulse bus
 * (i.e. it is built-in / hardwired and has no independent online/offline state).
 */
export function isIntegratedSensor(
  sensor: { sourceAddressMSB: number; sourceAddressLSB: number },
): boolean {
  return sensor.sourceAddressMSB <= 1 && sensor.sourceAddressLSB <= 1;
}

/**
 * Finds the sensorMapList entry that matches the given source address.
 * Useful for resolving the sensor's display name or type.
 */
export function findSensorMapEntry(
  sensorMapList: NASensorMapObject[],
  sensor: { sourceAddressMSB: number; sourceAddressLSB: number },
): NASensorMapObject | undefined {
  return sensorMapList.find(
    (entry) =>
      entry.sourceAddressMSB === sensor.sourceAddressMSB &&
      entry.sourceAddressLSB === sensor.sourceAddressLSB,
  );
}

/**
 * Resolves a numeric or string NAPhysicalSensorType to a human-readable label.
 */
export function resolveSensorTypeName(sensorType: unknown): string {
  if (sensorType === 0 || sensorType === 'WATER_SENSOR_TYPE') return 'Nevata';
  if (sensorType === 1 || sensorType === 'DC_SENSOR_TYPE_SINGLE_INSTANCE') return 'DC (Single)';
  if (sensorType === 2 || sensorType === 'LOOKOUT_SENSOR_TYPE') return 'XPulse';
  if (sensorType === 3 || sensorType === 'TENDER_SENSOR_TYPE') return 'Tender';
  if (sensorType === 4 || sensorType === 'DC_SENSOR_TYPE') return 'DC';
  if (sensorType === 5 || sensorType === 'ROUTER_SENSOR_TYPE') return 'Router';
  if (sensorType === 6 || sensorType === 'SWITCH_SENSOR_TYPE') return 'Switch';
  return String(sensorType);
}

// NAPhysicalSensorType wire ordinals (NAObject.cs: enum NAPhysicalSensorType : byte).
// The TS enum in types/enums is a string enum for readability, but the real API
// (and mock fixtures inconsistently) may send the raw numeric value — always
// compare with isSensorType() below rather than `sensor.sensorType === SomeEnum.X`.
const SENSOR_TYPE_ORDINAL: Record<string, number> = {
  WATER_SENSOR_TYPE: 0,
  DC_SENSOR_TYPE_SINGLE_INSTANCE: 1,
  LOOKOUT_SENSOR_TYPE: 2,
  TENDER_SENSOR_TYPE: 3,
  DC_SENSOR_TYPE: 4,
  ROUTER_SENSOR_TYPE: 5,
  SWITCH_SENSOR_TYPE: 6,
};

/**
 * True if the sensor's sensorType matches the given NAPhysicalSensorType,
 * accepting either the raw numeric wire value or the TS string-enum name.
 */
export function isSensorType(
  sensor: { sensorType: unknown },
  type: string,
): boolean {
  return sensor.sensorType === type || sensor.sensorType === SENSOR_TYPE_ORDINAL[type];
}
